"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import Image from "next/image";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { parseIpfsUrl } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getFileInfoFromUrl } from "@/helpers/file.helpers";
import { AdditionalMedia, MemesSubmissionAdditionalInfoKey } from "@/components/waves/memes/submission/types/OperationalData";
const MAX_MEDIA = 4;
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "m4v", "webm", "ogv"]);
const emptyAdditionalMedia: AdditionalMedia = {
  artist_profile_media: [],
  artwork_commentary_media: []
};
const parseAdditionalMedia = (rawValue?: string): AdditionalMedia => {
  if (!rawValue) {
    return emptyAdditionalMedia;
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<AdditionalMedia> | null;
    if (!parsed || typeof parsed !== "object") {
      return emptyAdditionalMedia;
    }
    return {
      artist_profile_media: Array.isArray(parsed.artist_profile_media) ? parsed.artist_profile_media.filter(Boolean) : [],
      artwork_commentary_media: Array.isArray(parsed.artwork_commentary_media) ? parsed.artwork_commentary_media.filter(Boolean) : []
    };
  } catch {
    return emptyAdditionalMedia;
  }
};
const isVideoUrl = (url: string) => {
  const fileInfo = getFileInfoFromUrl(url);
  if (!fileInfo?.extension) {
    return false;
  }
  return VIDEO_EXTENSIONS.has(fileInfo.extension.toLowerCase());
};
interface WaveDropAdditionalInfoProps {
  readonly drop: ExtendedDrop;
}
export const WaveDropAdditionalInfo = t0 => {
  const $ = _c(15);
  const {
    drop
  } = t0;
  let commentaryValue;
  let t1;
  if ($[0] !== drop.metadata) {
    const metadata = drop.metadata ?? [];
    const commentaryEntry = metadata.find(_temp);
    const additionalMediaEntry = metadata.find(_temp2);
    commentaryValue = commentaryEntry?.data_value?.trim() ?? "";
    const additionalMedia = parseAdditionalMedia(additionalMediaEntry?.data_value);
    t1 = additionalMedia.artwork_commentary_media.filter(_temp3).map(_temp4).filter(_temp5).map(_temp6);
    $[0] = drop.metadata;
    $[1] = commentaryValue;
    $[2] = t1;
  } else {
    commentaryValue = $[1];
    t1 = $[2];
  }
  const mediaItemsValue = t1;
  let t2;
  if ($[3] !== commentaryValue || $[4] !== mediaItemsValue) {
    t2 = {
      commentary: commentaryValue,
      mediaItems: mediaItemsValue
    };
    $[3] = commentaryValue;
    $[4] = mediaItemsValue;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    commentary,
    mediaItems
  } = t2;
  let t3;
  let t4;
  if ($[6] !== commentary || $[7] !== mediaItems) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const displayedMedia = mediaItems.slice(0, MAX_MEDIA);
      if (!commentary && displayedMedia.length === 0) {
        t4 = null;
        break bb0;
      }
      let t5;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <h2 className="tw-text-lg tw-font-semibold tw-text-iron-100">{"Additional Media & Notes"}</h2>;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      let t6;
      if ($[11] !== commentary) {
        t6 = commentary && <p className="tw-mb-0 tw-text-sm tw-text-iron-400 tw-leading-relaxed">{commentary}</p>;
        $[11] = commentary;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      let t7;
      if ($[13] !== t6) {
        t7 = <div className="tw-space-y-2">{t5}{t6}</div>;
        $[13] = t6;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      t3 = <section className="tw-space-y-6">{t7}{displayedMedia.length > 0 && <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 md:tw-gap-4">{displayedMedia.map(_temp7)}</div>}</section>;
    }
    $[6] = commentary;
    $[7] = mediaItems;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  return t3;
};
function _temp(item) {
  return item.data_key === MemesSubmissionAdditionalInfoKey.COMMENTARY;
}
function _temp2(item_0) {
  return item_0.data_key === MemesSubmissionAdditionalInfoKey.ADDITIONAL_MEDIA;
}
function _temp3(url) {
  return typeof url === "string" && url.trim().length > 0;
}
function _temp4(url_0) {
  return parseIpfsUrl(url_0);
}
function _temp5(url_1) {
  return url_1.length > 0;
}
function _temp6(url_2) {
  const isVideo = isVideoUrl(url_2);
  const displayUrl = isVideo ? url_2 : getScaledImageUri(url_2, ImageScale.AUTOx600);
  return {
    url: url_2,
    displayUrl,
    isVideo
  };
}
function _temp7(item_1, index) {
  return <div key={`${item_1.url}-${index}`} className={`tw-relative tw-overflow-hidden tw-bg-white/[0.02] ${item_1.isVideo ? "tw-aspect-video sm:tw-col-span-2" : "tw-aspect-[4/3]"}`}>{item_1.isVideo ? <video src={item_1.url} className="tw-h-full tw-w-full tw-object-cover" controls={true} preload="metadata"><track kind="captions" src="data:text/vtt,WEBVTT" srcLang="en" label="Captions" /></video> : <Image src={item_1.displayUrl} alt={`Process media ${index + 1}`} fill={true} sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" className="tw-object-contain" />}</div>;
}